import React, { useRef, useState, useLayoutEffect, useEffect } from 'react';

import Box from '@mui/material/Box';
import InputAdornment from '@mui/material/InputAdornment';
import TextField from '@mui/material/TextField';
import type { TextFieldProps } from '@mui/material/TextField';
import selectPrivacyModeEnabled from 'app-components/hooks/privacyMode/selectPrivacyModeEnabled';
import { useAppSelector } from 'hooks/redux';
import { makeStyles } from 'tss-react/mui';

const useStyles = makeStyles()(() => ({
  privacyMask: {
    WebkitTextSecurity: 'disc',
  },
}));

type PercentageFieldProps = Omit<TextFieldProps, 'onChange'> & {
  value: string;
  onChange: (value: string) => void;
  /** Optional node rendered inside an end adornment (e.g. an info icon). Omit to render nothing. */
  endAdornment?: React.ReactNode;
  /** Extra gap in px between the last digit and the % sign. Defaults to 4. */
  percentGap?: number;
  /** When true, the field masks its value while privacy mode is enabled. */
  usePrivacyMode?: boolean;
};

const PercentageField = ({
  value,
  onChange,
  onFocus,
  onBlur,
  endAdornment,
  percentGap = 4,
  style,
  usePrivacyMode = false,
  ...textFieldProps
}: PercentageFieldProps) => {
  const [focused, setFocused] = useState(false);
  const { classes } = useStyles();
  const privacyModeEnabled = useAppSelector(selectPrivacyModeEnabled);
  const isPrivacyMasked = usePrivacyMode && privacyModeEnabled;
  const supportsWebkitTextSecurity =
    typeof window !== 'undefined' && window.CSS?.supports?.('-webkit-text-security', 'disc');
  const privacyModeProps =
    isPrivacyMasked && !supportsWebkitTextSecurity
      ? {
          type: 'password',
          autoComplete: 'one-time-code',
          'data-1p-ignore': 'true',
          'data-bwignore': 'true',
          'data-lpignore': 'true',
        }
      : { type: 'text' };

  // Refs for DOM measurement — not stored in state because they don't drive re-renders directly.
  const boxRef = useRef<HTMLDivElement>(null); // Full field height and width
  const inputRef = useRef<HTMLInputElement>(null); // Just the input itself

  // A hidden off-screen <span> used to measure pixel widths of text strings.
  // We copy the input's computed font onto it, set its textContent, and read
  // getBoundingClientRect().width — the browser does the measurement for us.
  const mirrorRef = useRef<HTMLSpanElement>(null);

  // Cached measurements updated each layout pass, read during render.
  const percentWidthRef = useRef(0);
  const fontSizeRef = useRef('16px');

  // The absolute position of the % overlay relative to the Box wrapper.
  const [percentPos, setPercentPos] = useState({ left: 0, top: 0 });

  // Holds the latest measurement function so the ResizeObserver below can call
  // it without capturing stale closure values or re-subscribing on every render.
  const measureRef = useRef<() => void>(() => undefined);

  // useLayoutEffect (not useEffect) so position is calculated and applied before
  // the browser paints — prevents any frame where the % is in the wrong spot.
  useLayoutEffect(() => {
    const measure = () => {
      const input = inputRef.current;
      const mirror = mirrorRef.current;
      const box = boxRef.current;
      if (!input || !mirror || !box) return;

      // Mirror the input's font so measurements match what the user sees.
      const computedStyle = window.getComputedStyle(input);
      mirror.style.font = computedStyle.font;
      mirror.style.letterSpacing = computedStyle.letterSpacing;
      fontSizeRef.current = computedStyle.fontSize;

      // Measure the % character width so we know how much space to reserve.
      mirror.textContent = '%';
      percentWidthRef.current = mirror.getBoundingClientRect().width;

      // Measure the current value's text width to find where the % should start.
      mirror.textContent = value;
      const textWidth = mirror.getBoundingClientRect().width;

      // Calculate the vertical center of the input's text area, accounting for
      // the top padding MUI uses to make room for the floating label.
      const paddingLeft = parseFloat(computedStyle.paddingLeft) || 12;
      const paddingTop = parseFloat(computedStyle.paddingTop) || 25;
      const paddingBottom = parseFloat(computedStyle.paddingBottom) || 8;
      const inputRect = input.getBoundingClientRect();
      const boxRect = box.getBoundingClientRect();
      const textCenterY = paddingTop + (inputRect.height - paddingTop - paddingBottom) / 2;

      // Clamp so the % never escapes past the native <input>'s right edge,
      // which is where any end adornments begin. The 4px buffer keeps a small
      // gap between the % and the adornment.
      const unclamped = paddingLeft + textWidth + percentGap;
      const maxLeft = inputRect.right - boxRect.left - percentWidthRef.current - 4;

      setPercentPos({
        left: Math.min(unclamped, maxLeft),
        top: inputRect.top - boxRect.top + textCenterY,
      });
    };

    measureRef.current = measure;
    measure();
  }, [value, focused, percentGap]);

  // Re-measure whenever the Box changes size (drawer resize, responsive layout,
  // late font load) so the % doesn't drift until the user types again.
  useEffect(() => {
    const box = boxRef.current;
    if (!box) return;
    const observer = new ResizeObserver(() => measureRef.current());
    observer.observe(box);
    return () => observer.disconnect();
  }, []);

  const showPercent = focused || !!value;

  return (
    // Position relative so the % overlay can be placed absolutely within this wrapper.
    <Box ref={boxRef} sx={{ position: 'relative' }} style={style}>
      {/* Hidden mirror span — lives off-screen and is never visible to the user. */}
      <span
        ref={mirrorRef}
        style={{ position: 'fixed', visibility: 'hidden', whiteSpace: 'pre', top: -9999, left: -9999 }}
      />

      <TextField
        fullWidth
        {...textFieldProps}
        {...privacyModeProps}
        value={value}
        onChange={(e) => {
          // Strip anything that isn't a digit or decimal point, and collapse
          // multiple decimal points down to one (e.g. "12..5" → "12.5").
          const raw = e.target.value.replace(/[^0-9.]/g, '');
          const parts = raw.split('.');
          onChange(parts.length > 2 ? `${parts[0]}.${parts.slice(1).join('')}` : raw);
        }}
        onFocus={(e) => {
          setFocused(true);
          onFocus?.(e);
        }}
        onBlur={(e) => {
          setFocused(false);
          onBlur?.(e);
        }}
        inputRef={inputRef}
        slotProps={(() => {
          const inputSlot =
            typeof textFieldProps.slotProps?.input !== 'function' ? textFieldProps.slotProps?.input : undefined;
          const htmlInputSlot =
            typeof textFieldProps.slotProps?.htmlInput !== 'function' ? textFieldProps.slotProps?.htmlInput : undefined;
          return {
            ...textFieldProps.slotProps,
            input: {
              ...inputSlot,
              endAdornment: endAdornment ? (
                <InputAdornment position='end'>{endAdornment}</InputAdornment>
              ) : (
                inputSlot?.endAdornment
              ),
            },
            // Reserve right padding equal to the % width + gap so typed text
            // never slides underneath the % overlay.
            htmlInput: {
              ...htmlInputSlot,
              className: isPrivacyMasked && supportsWebkitTextSecurity ? classes.privacyMask : undefined,
              style: {
                ...(htmlInputSlot?.style as React.CSSProperties),
                paddingRight: percentWidthRef.current + percentGap + 4,
              },
            },
          };
        })()}
      />

      {/* The % sign, absolutely positioned just to the right of the typed value. */}
      {showPercent && (
        <span
          aria-hidden='true'
          style={{
            position: 'absolute',
            left: percentPos.left,
            top: percentPos.top,
            transform: 'translateY(-50%)',
            opacity: 0.5,
            pointerEvents: 'none',
            fontSize: fontSizeRef.current,
            lineHeight: '1',
          }}
        >
          %
        </span>
      )}
    </Box>
  );
};

export default PercentageField;
